<template lang="pug">
.product-price(:class="theme")
  .price(v-if="!onlyExclVat &&  theme !== 'wishlist'&& theme !== 'checkout'") {{ rogue.currency(showTotalPrice ? totalPriceInclVat : singlePriceInclVat, { allowLocalCurrency: theme !== 'checkout' }) }}
    | {{ subscriptionInterval.toUpperCase() }}
  .price.trial-wishlist(v-if="theme === 'wishlist' || theme === 'checkout'") {{ rogue.currency(specialPrice) }}
  .single-price(v-if="showSinglePrice") @ {{ formatSinglePrice }}{{ subscriptionInterval }}
  .old-price(v-if="showOldPrice") {{ rogue.currency(mainProduct.price, { euTaxClass: taxClass }) }}
  div(:class="(specialPrice !== undefined) ? 'old-price' : 'price'")
  .break(v-if="theme && ['category', 'search', 'list'].includes(theme)")
  .excluding-tax-container(v-if="taxClass && showExclVat && singlePrice < singlePriceInclVat")
    span.tax-label {{theme && ['category','list'].includes(theme) ? __('Excluding VAT') : __('Excl. VAT') }}
    .tax-excl {{ rogue.currency(showTotalPrice ? totalPrice : singlePrice) }}
</template>
<script setup lang="ts">
import { computed, watch } from 'vue'
import type { PropType } from 'vue'
import type { RogueOrder } from '@orders'
import { TaxClass, getPriceInclTax } from '@rogue/universal'
import { useRogue } from '@/js/services/Rogue'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useProductStore } from '@/js/stores/productStore'
import { computePriceSync } from '@/js/helpers/price-utils'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'

let getOrder: (() => RogueOrder | null) | undefined

const props = defineProps({
  product: {
    type     : Object as PropType<Product>,
    required : false,
  },
  buyRequest: {
    type     : Object as PropType<BuyRequest>,
    required : false,
  },
  showTotalPrice: {
    type    : Boolean,
    default : true,
  },
  showSinglePrice: {
    type    : Boolean,
    default : false,
  },
  hideNormalPrice: {
    type    : Boolean,
    default : false,
  },
  showExclVat: {
    type    : Boolean,
    default : false,
  },
  onlyExclVat: {
    type    : Boolean,
    default : false,
  },
  theme: {
    type     : String,
    required : false,
  },
})

const rogue = useRogue()
const productStore = useProductStore()

watch(() => rogue.loaded.checkout, (v) => {
  if (v) import('@/js/pages/checkout/checkout').then(m => { getOrder = m.getOrder })
})

const products = computed(() => productStore.products || [])

const mainProduct = computed<Product | undefined>(() => {
  if (props.product) {
    return props.product
  }

  let buyRequest = props.buyRequest

  let product = buyRequest?.productId ? products.value[buyRequest.productId] || undefined : undefined
  while (buyRequest?.children?.length) {
    // If this is a bundle or if it has > 1 qty we need to just compute the buy request's quantity
    if (!product || Object.values(buyRequest.options || {}).length || buyRequest.children.length > 1) {
      return undefined
    }

    buyRequest = buyRequest.children[0]
    product = products.value[buyRequest.productId]
  }

  return product || undefined
})

const isProgram = computed(() => mainProduct.value?.isProgram)
const isTrial = computed(() => mainProduct.value?.attributes?.is_trial)
const isRewardTrialItem = computed(() => mainProduct.value?.attributes?.ticket_required && mainProduct.value?.attributes?.trial_duration_days)
const showOldPrice = computed(() => props.theme === 'wishlist' || props.theme === 'checkout' || isRewardTrialItem.value)

const taxClass = computed<TaxClass | undefined>(() => {
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    return undefined
  }

  let taxClassVal = mainProduct.value?.attributes?.tax_class_id

  if (!taxClassVal && props.buyRequest) {
    // If the main product has no tax class then find the first simple
    taxClassVal = getSimpleForBuyRequest(props.buyRequest, rogue)?.attributes?.tax_class_id
  }

  if (!taxClassVal && props.product?.productId) {
    taxClassVal = products.value[props.product?.productId]?.attributes.tax_class_id
  }

  return taxClassVal as TaxClass
})

const subscriptionInterval = computed<string>(() => {
  if (mainProduct.value?.attributes?.subscription_interval) {
    if (mainProduct.value?.attributes?.subscription_interval === 'Month') {
      return '/mo'
    }

    return `/${mainProduct.value.attributes.subscription_interval}`
  }

  return ''
})

const specialPrice = computed(() => {
  const product = mainProduct.value

  if (!product?.productId) {
    return undefined
  }

  const specialPrices = [product.special_price]
    .filter(val => (val === 0 || !!val) && product.price && Number(val) < product.price) as number[]

  const min =  Math.min(...specialPrices)

  return specialPrices.length ? min : undefined
})

const singlePrice = computed<string | number>(() => {
  let price: string | number = '--'

  // If the product is a Reward Trial Item, we need to calculate the discounted price
  // else set the trial price on non-reward trial programs
  if (props.buyRequest && isRewardTrialItem.value) {
    try {
      price = computePriceSync({ ...props.buyRequest, qty: 1 }, rogue.pinia!)
    } catch (e) {
      rogue.log.warn('Error computing trial price', e as Error)
    }
  } else {
    price = mainProduct.value?.price || price
  }

  return price
})

const singlePriceInclVat = computed(() => {
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    return singlePrice.value
  }

  if (rogue.loaded.checkout && getOrder?.()?.state.tax.isTaxExempt) {
    return singlePrice.value
  }

  if (typeof singlePrice.value === 'string') {
    return singlePrice.value
  }

  return getPriceInclTax(singlePrice.value, taxClass.value, rogue.l10n.country || undefined)
})

const totalPrice = computed(() => {
  if (typeof singlePrice.value === 'string') {
    return singlePrice.value
  }

  return (props.buyRequest?.qty || 1) * singlePrice.value
})

const totalPriceInclVat = computed(() => {
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    return totalPrice.value
  }

  if (typeof singlePriceInclVat.value === 'string') {
    return singlePriceInclVat.value
  }

  return (props.buyRequest?.qty || 1) * singlePriceInclVat.value
})

const formatSinglePrice = computed(() => rogue.currency(isRewardTrialItem.value ? mainProduct.value.price : singlePriceInclVat.value, { allowLocalCurrency: props.theme !== 'checkout' }))

</script>

<style lang="scss" scoped>
.price {
  font-size: $size-xl;
  font-weight: $weight-bold;
  font-family: $font-base;
}

.product-price {
  &.rhpa {
    display: flex;
    align-items: center;
    margin-bottom: $space-xxs;
    font-size: $size-l;

    .old-price {
      margin-left: $space-xs;
      color: $color-black-light;
      font-size: $size-s;
      text-decoration: line-through;
    }

    .price {
      line-height: $line-height-s;
      font-weight: $weight-bold;
      font-size: $size-xl;
    }

    .excluding-tax-container {
      color: $color-gray-x-dark;
      font-style: italic;
      line-height: 1;
    }

    .tax-excl {
      font-weight: bold;
      font-size: $size-xxs;
      margin-left: $space-xs;
    }

    .tax-label {
      font-weight: 300;
      font-size: $size-xxs;
      margin-right: $space-xxxs;
      font-weight: $weight-light;
    }
  }
}

.wishlist, .checkout {
  display: flex;
  flex-direction: column;
  margin-left: $size-s;

  .old-price {
    display: flex;
    flex-direction: row-reverse;
  }

  .price {
    font-weight: $weight-bold;
    font-size: $size-s;

    &.trial-wishlist {
       margin-left: auto;
      }
  }

  .single-price {
    display:flex;
    font-size: $size-xxs;
    order: 3;
    text-align: end;
    font-size: 11px;
  }

  .excluding-tax-container {
    color: $color-gray-x-dark;
    font-size: 12px;
    line-height: 1;
    display: block;
  }

  .tax-excl {
    display: flex;
    flex-direction: row-reverse;
  }

  .product-price.rhpa-child {
    min-width: 118px;
  }
}

.break {
  height: 0;
  flex-basis: 100%;
}

.product-price {
  color: $color-black;
}

.old-price {
  font-size: $size-xxs;
  color: $color-gray-dark;
  text-decoration: line-through;
}

</style>
